import { Material, Mesh, Object3D } from "three";

/** JSON file schema */
export interface OutfitMetadata {
  items: string[];
}

export type OutfitHandedness = "Right" | "Left";

/**
 * Allows for selection of level of detail (LOD) class and sub-meshes in master
 * GLTF.
 *
 * Stores what submeshes (items) are enabled in the `_items` map.
 */
export class Outfit {
  processMaterial?: (m: Material) => void;

  private _items: Map<string, boolean> = new Map();

  constructor(
    lod?: number,
    items?: string[],
    processMaterial?: (m: Material) => void
  ) {
    if (processMaterial) this.processMaterial = processMaterial;

    if (items) {
      for (const item of items) {
        this.setItem(item, true);
      }
    }
  }

  /**
   * Get whether an item is enabled or not
   * @param name item name (mesh name in GLTF)
   * @returns enabled bool
   */
  getItem(name: string): boolean {
    return this._items.get(name) ?? false;
  }

  /**
   * Set item enabled state
   * @param name item name
   * @param on enabled bool
   */
  setItem(name: string, on: boolean, handed?: OutfitHandedness) {
    if (name.includes("_LOD")) {
      name = name.slice(0, -5);
    }
    this._items.set(name, on);
  }

  /**
   * Get list of all items the manager is aware of, or has flagged as enabled
   * @param getEnabled whether to just return enabled items
   * @returns array of item names
   */
  itemList(getEnabled = false): string[] {
    const keys = [...this._items.keys()].sort();
    if (getEnabled) {
      return keys.filter((n) => this.getItem(n));
    } else {
      return keys;
    }
  }

  /**
   * Apply outfit to a model Object3D
   *
   * Can be temporary (makes the non-selected items invisible) or permanent
   *
   * @param targets GLTF targets to alter
   * @param temporary whether to make the change permanent or temporary
   */
  apply(
    targets: Object3D[],
    temporary: boolean = false,
    handedness: OutfitHandedness = "Right",
    lod: number = 0
  ): void {
    const toRemove: Mesh[] = [];

    for (const target of targets) {
      target.traverse((object) => {
        const mesh = object as Mesh;
        if (mesh.isMesh) {
          if (this.processMaterial && !Array.isArray(mesh.material)) {
            this.processMaterial(mesh.material);
          }
          mesh.castShadow = true;
          mesh.receiveShadow = true;

          const meshName = mesh.name.includes("___")
            ? mesh.name.split("___")[0]
            : mesh.name;
          const [meshNamePrefix, meshLod] = meshName.split("_LOD");

          let present = this._items.get(meshNamePrefix);
          let handed: string | undefined = undefined;
          if (!present) {
            if (
              this._items.get(
                meshNamePrefix.replace("Right", "*").replace("Left", "*")
              )
            ) {
              handed = "mirrored";
            } else if (
              this._items.get(
                meshNamePrefix.replace("Right", "^").replace("Left", "^")
              )
            ) {
              handed = "normal";
            }
          }

          // handle handedness
          if (handed) {
            const searchFor = handed == "mirrored" ? "Right" : "Left";
            const hand = handedness == searchFor ? "Left" : "Right";
            if (meshNamePrefix.includes(hand)) {
              present = true;
            }
          }

          if (!meshNamePrefix || !meshLod) {
            console.error(`mesh ${mesh.name} doesn't follow naming convention`);
          } else {
            let remove = false;
            if (Number(meshLod) != lod || !present) {
              remove = true;
            }

            if (temporary) {
              mesh.visible = !remove;
            } else if (remove) {
              toRemove.push(mesh);
            }
          }
        }
      });
    }

    if (!temporary) {
      for (let mesh of toRemove) {
        mesh.geometry.dispose();
        //@ts-ignore
        mesh.material.dispose();
        mesh.parent?.remove(mesh);
      }
    }
  }

  /**
   * Clear outfit items
   */
  clear() {
    this._items.clear();
  }

  /**
   * Serialise object to JSON - for export
   * @returns json string of form OutfitMetadata
   */
  toJSON(): string {
    const itemList = this.itemList(true);
    return (
      JSON.stringify(
        {
          items: itemList,
        },
        null,
        2
      ) + "\n"
    );
  }

  /**
   * Parse json object as Outfit
   * @param json
   * @returns this post parse
   */
  fromJSON(json: OutfitMetadata): Outfit {
    this.clear();

    for (const item of json.items) {
      this.setItem(item, true);
    }

    return this;
  }
}
